/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {closeModal, closeSecondaryModal, setHeadbarTitle,} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import BookingAction from 'store/booking/BookingAction'
import OfferBasicForm from 'components/Offers/forms/OfferBasicForm'
import OfferEventsForm from 'components/Offers/forms/OfferEventsForm'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiStepsNav, {UiStepsNavStep} from 'components/Forms/UiStepsNav'
import clsx from 'clsx'
import OfferUpsells from './OfferUpsells'
import {Offer} from 'types/entities/Offer'
import {useTypedSelector} from 'helpers/reducers'
import {OffersAction} from 'store/offers/OffersAction'
import {toast} from 'utils/toastUtils'
import {OfferBasicInputs} from 'components/Offers/schemas/offerBasicSchema'
import {BookingFilter} from 'types/app'
import {getNextOfferCalculationIndex} from 'utils/offers/offersUtils'
import type {OfferCalculation} from 'types/entities/OfferCalculation'

enum CreateOfferCalculationStep {
    Details = 1,
    PackageItems = 2,
    Upsells = 3,
}

interface CreateOfferCalculationModalProps {
    onClose?: () => void
    offer_id: number
    onCreated?: (offer?: Offer) => void
    is_secondary?: boolean
    defaultValues?: Partial<OfferBasicInputs>
    calculationId?: number
}

const CreateOfferCalculationModal: React.FC<
    CreateOfferCalculationModalProps
> = (props) => {
    const {
        offer_id: offerId,
        onCreated,
        onClose,
        is_secondary: isSecondary,
        defaultValues,
        calculationId,
    } = props
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const offerLoadErrorMessage = t('Failed to load offer detail')

    const selectedOffer = useTypedSelector(
        (state) => state.offersState.selectedOffer
    )
    const sellerId = useTypedSelector(
        (state) => state.sessionState.session?.uid
    )

    const packageCart = useTypedSelector(
        (state) => state.bookingState.bookingProcess.packageCart
    )

    const bookingProcess = useTypedSelector(
        (state) => state.bookingState.bookingProcess
    )
    const selectedPackageItems = useTypedSelector(
        (state) => state.bookingState.bookingProcess.selectedPackageItems
    )
    const bookingFilter = useTypedSelector(
        (state) => state.bookingState.bookingProcess.filter
    )
    const bookingFilterRef = React.useRef(bookingFilter)

    useEffect(() => {
        bookingFilterRef.current = bookingFilter
    }, [bookingFilter])

    const applyCalculationToBookingProcess = useCallback(
        (calc: OfferCalculation) => {
            if (!calc.packagesCart) {
                return
            }

            const currentFilter = bookingFilterRef.current
            const nextFilter: BookingFilter = {
                ...currentFilter,
                date: calc.date || currentFilter.date,
                adults:
                    calc.packagesCart.drivers_count_adult || currentFilter.adults,
                children:
                    calc.packagesCart.drivers_count_child ||
                    currentFilter.children,
                timeslot_id:
                    calc.packagesCart.packagesCartItemSlots?.[0]?.timeslot_id ??
                    currentFilter.timeslot_id ??
                    null,
                category: calc.packagesCart.category ?? currentFilter.category ?? null,
            }

            dispatch(BookingAction.setPackageCart(calc.packagesCart))
            dispatch(BookingAction.setFilter(nextFilter))
        },
        [dispatch]
    )

    useEffect(() => {
        if (!calculationId || !selectedOffer) {
            return
        }

        const calc = selectedOffer.calculations?.find(
            (calculation) => calculation.id === calculationId
        )
        if (!calc) {
            return
        }

        applyCalculationToBookingProcess(calc)
    }, [applyCalculationToBookingProcess, calculationId, selectedOffer])

    const [activeStep, setActiveStep] = useState<CreateOfferCalculationStep>(
        CreateOfferCalculationStep.Details
    )
    const [maxReachedStep, setMaxReachedStep] =
        useState<CreateOfferCalculationStep>(CreateOfferCalculationStep.Details)

    const steps: UiStepsNavStep<CreateOfferCalculationStep>[] = [
        {
            id: CreateOfferCalculationStep.Details,
            label: t('Details'),
        },
        {
            id: CreateOfferCalculationStep.PackageItems,
            label: t('Package items'),
        },
        {
            id: CreateOfferCalculationStep.Upsells,
            label: t('Upsells'),
        },
    ]

    const handleSuccess = useCallback(
        (offer?: Offer) => {
            if (!offer) return
            if (onCreated) {
                onCreated(offer)
            }

            if (onClose) {
                onClose()
            } else if (isSecondary) {
                dispatch(closeSecondaryModal())
            } else {
                dispatch(closeModal())
            }
        },
        [dispatch, onCreated, isSecondary, onClose]
    )

    const [basicValues, setBasicValues] = useState<OfferBasicInputs | null>(
        null
    )
    const [pendingCalculationName, setPendingCalculationName] = useState<string | null>(null)

    const handleOfferItemsSubmit = async () => {
        if (
            !selectedOffer ||
            !packageCart ||
            !sellerId ||
            !bookingProcess.filter.category ||
            !bookingProcess.filter.timeslot_id
        ) {
            toast.error(t('Please select category and timeslot'))
            return
        }

        try {
            const response = await dispatch(
                BookingAction.fetchOfferPackageNew(
                    {
                        package_category_name: bookingProcess.filter.category,
                        package_item_ids: selectedPackageItems
                            .map((item) => item.id)
                            .filter((id): id is number => id !== null),
                        activity_ids:
                            bookingProcess.filter.activity_ids.length === 0
                                ? [1]
                                : bookingProcess.filter.activity_ids,
                        timeslot_id: bookingProcess.filter.timeslot_id,
                        adults: bookingProcess.filter.adults,
                        children: bookingProcess.filter.children,
                        upsell: [], // Upsells are handled in the next step
                        seller_id: sellerId,
                    },
                    packageCart,
                    selectedOffer,
                    'CreateOfferCalculationModal'
                )
            )

            // Update probability if needed
            if (response?.offer) {
                const calc = response.offer.calculations?.find(
                    (c) => c.packages_cart_id === packageCart.id
                )
                if (calc) {
                    await dispatch(
                        OffersAction.fetchAddOrEditOfferCalculation(
                            calc.id,
                            selectedOffer.id,
                            sellerId,
                            {
                                name: pendingCalculationName || undefined,
                                probability: basicValues?.probability ?? 50,
                                packages_cart_id: packageCart.id,
                            }
                        )
                    )
                }
            }

            setActiveStep(CreateOfferCalculationStep.Upsells)
            setMaxReachedStep(CreateOfferCalculationStep.Upsells)
        } catch (error) {
            console.error(error)
            toast.error(t('Failed to add offer items'))
        }
    }

    const handleCreateCalculationFromFirstStep = async (
        values: OfferBasicInputs
    ) => {
        if (!selectedOffer || !sellerId) {
            return
        }

        setBasicValues(values)

        if (calculationId) {
            setActiveStep(CreateOfferCalculationStep.PackageItems)
            setMaxReachedStep(CreateOfferCalculationStep.PackageItems)
            return
        }

        const nextCalculationIndex = getNextOfferCalculationIndex(selectedOffer.calculations)
        setPendingCalculationName(String(nextCalculationIndex))

        try {
            const initCartResponse = await dispatch(
                BookingAction.initSellerCart(true, sellerId)
            )

            if (!initCartResponse?.packageCart?.id) {
                toast.error(t('Failed to initialize cart'))
                return
            }

            setActiveStep(CreateOfferCalculationStep.PackageItems)
            setMaxReachedStep(CreateOfferCalculationStep.PackageItems)
        } catch {
            toast.error(t('An error occurred during calculation creation'))
        }
    }

    useLayoutEffect(() => {
        dispatch(BookingAction.clearBookingProcessState())
        if (!onClose) dispatch(OffersAction.resetSelectedOffer())
    }, [dispatch, offerId, onClose])

    useEffect(() => {
        if (!offerId) return
        void dispatch(OffersAction.fetchOfferById(offerId)).catch(() => toast.error(offerLoadErrorMessage))
    }, [dispatch, offerId, offerLoadErrorMessage])

    useEffect(() => {
        if (onClose) return
        const title = calculationId
            ? t('Edit Calculation')
            : t('Create Calculation')
        dispatch(setHeadbarTitle(title))
        return () => {
            dispatch(setHeadbarTitle(null))
        }
    }, [dispatch, t, calculationId, onClose])

    const computedDefaultValues = useMemo(() => {
        if (defaultValues) return defaultValues
        if (!selectedOffer) return undefined

        const targetCalc = calculationId
            ? selectedOffer.calculations?.find((calculation) => calculation.id === calculationId)
            : selectedOffer.calculations?.find((c) => c.is_target) ??
              selectedOffer.calculations?.[0]

        if (targetCalc?.packagesCart) {
            return {
                date: targetCalc.date ?? targetCalc.packagesCart.date ?? selectedOffer.input?.date,
                adults:
                    targetCalc.packagesCart.drivers_count_adult ??
                    selectedOffer.input?.drivers_count_adult ??
                    0,
                children:
                    targetCalc.packagesCart.drivers_count_child ??
                    selectedOffer.input?.drivers_count_child ??
                    0,
                contact_id: selectedOffer.contact_id ?? undefined,
                probability: selectedOffer.probability ?? 50,
            }
        }

        return {
            date: selectedOffer.input?.date,
            adults: selectedOffer.input?.drivers_count_adult ?? 0,
            children: selectedOffer.input?.drivers_count_child ?? 0,
            contact_id: selectedOffer.contact_id ?? undefined,
            probability: selectedOffer.probability ?? 50,
        }
    }, [calculationId, defaultValues, selectedOffer])

    const effectiveCompanyId = basicValues?.company_id ?? selectedOffer?.company_id ?? null

    const selectedCompanyForForm = useMemo(() => {
        if (!selectedOffer?.company || !effectiveCompanyId) {
            return null
        }

        return Number(selectedOffer.company.id) === Number(effectiveCompanyId)
            ? selectedOffer.company
            : null
    }, [selectedOffer?.company, effectiveCompanyId])

    const mergedDefaultValues = useMemo<Partial<OfferBasicInputs> | undefined>(() => {
        return {
            ...(computedDefaultValues ?? {}),
            ...(basicValues ?? {}),
        }
    }, [computedDefaultValues, basicValues])

    return (
        <UiFormModalShell
            className={clsx('CreateOfferCalculationModal', 'dark:bg-dark-950')}
            title={
                calculationId ? t('Edit Calculation') : t('Create Calculation')
            }
            subtitle={t('Fill in the calculation details below')}
            headerBottom={
                <UiStepsNav
                    activeStep={activeStep}
                    maxReachedStep={maxReachedStep}
                    onChange={setActiveStep}
                    steps={steps}
                />
            }
        >
            <div
                className={clsx(
                    'CreateOfferCalculationModal-content w-full mx-auto flex flex-col space-y-6',
                    'dark:text-white'
                )}
            >
                {activeStep === CreateOfferCalculationStep.Details && (
                    <OfferBasicForm
                        onChange={handleCreateCalculationFromFirstStep}
                        companyIdFromContext={effectiveCompanyId}
                        selectedCompanyDefault={selectedCompanyForForm}
                        defaultValues={mergedDefaultValues}
                    />
                )}

                {activeStep === CreateOfferCalculationStep.PackageItems && (
                    <OfferEventsForm
                        onBack={() =>
                            setActiveStep(CreateOfferCalculationStep.Details)
                        }
                        onSuccess={handleOfferItemsSubmit}
                    />
                )}

                {activeStep === CreateOfferCalculationStep.Upsells && (
                    <OfferUpsells
                        onBack={() =>
                            setActiveStep(
                                CreateOfferCalculationStep.PackageItems
                            )
                        }
                        onSuccess={handleSuccess}
                        isCalculationMode={true}
                    />
                )}
            </div>
        </UiFormModalShell>
    )
}

export default CreateOfferCalculationModal
